import { ENTREPOT_PROXY } from "./constants.js";
import { ipfsUriToHttp, isBareIpfsCid } from "./ipfs.js";
const IMAGE_KEYS = [
    "image",
    "imageUrl",
    "image_url",
    "url",
    "thumbnail",
    "preview",
    "uri",
    "media",
];
/**
 * Wrapped EXT canisters → original collection (art often lives on the original).
 * From Toniq EXT legacy-support guide.
 */
export const EXT_WRAPPER_TO_ORIGINAL = {
    "bxdf4-baaaa-aaaah-qaruq-cai": "qcg3w-tyaaa-aaaah-qakea-cai", // ICPunks
    "y3b7h-siaaa-aaaah-qcnwa-cai": "4nvhy-3qaaa-aaaah-qcnoq-cai", // ICats
    "jeghr-iaaaa-aaaah-qco7q-cai": "fl5nr-xiaaa-aaaai-qbjmq-cai", // ICTurtles
    "q6hjz-kyaaa-aaaah-qcama-cai": "xkbqi-2qaaa-aaaah-qbpqq-cai", // ICPBunny
};
export { ENTREPOT_PROXY };
export function icpRawThumbnailUrl(canisterId, tokenIndex) {
    const idx = typeof tokenIndex === "bigint" ? tokenIndex.toString() : String(tokenIndex);
    return `https://${canisterId}.raw.icp0.io/?index=${idx}&type=thumbnail`;
}
export function icpRawAssetUrl(canisterId, tokenIndex) {
    const idx = typeof tokenIndex === "bigint" ? tokenIndex.toString() : String(tokenIndex);
    return `https://${canisterId}.raw.icp0.io/?index=${idx}`;
}
export function icpTokenIdAssetUrl(canisterId, extTokenId) {
    return `https://${canisterId}.raw.icp0.io/?tokenid=${encodeURIComponent(extTokenId)}`;
}
export function icpTokenIdThumbnailUrl(canisterId, extTokenId) {
    return `https://${canisterId}.raw.icp0.io/?tokenid=${encodeURIComponent(extTokenId)}&type=thumbnail`;
}
export function icpStaticThumbPathUrl(canisterId, tokenIndex) {
    const idx = typeof tokenIndex === "bigint" ? tokenIndex.toString() : String(tokenIndex);
    return `https://${canisterId}.raw.icp0.io/thumbnails/${idx}.png`;
}
export function icpEntrepotThumbnailUrl(canisterId, tokenIndex) {
    const idx = typeof tokenIndex === "bigint" ? tokenIndex.toString() : String(tokenIndex);
    return `https://${ENTREPOT_PROXY}.raw.icp0.io/?canister=${canisterId}&index=${idx}&type=thumbnail`;
}
export function resolveIcpNftImageUrl(raw) {
    if (!raw?.trim())
        return null;
    let trimmed = raw.trim();
    // Yuku CDN wraps the real asset: cdn.yuku.app/...?url=<encoded>
    try {
        if (/cdn\.yuku\.app/i.test(trimmed)) {
            const u = new URL(trimmed);
            const nested = u.searchParams.get("url");
            if (nested?.startsWith("http"))
                trimmed = nested;
        }
    }
    catch {
        /* keep trimmed */
    }
    if (trimmed.startsWith("data:"))
        return trimmed;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        return trimmed;
    }
    if (trimmed.startsWith("ipfs://") ||
        trimmed.startsWith("ar://") ||
        isBareIpfsCid(trimmed)) {
        return ipfsUriToHttp(trimmed);
    }
    return trimmed;
}
export function resolveIcpTokenImageUrl(canisterId, tokenIndex, metadataBlob) {
    const fromMeta = metadataBlob ? imageFromMetadataBlob(metadataBlob) : null;
    if (fromMeta)
        return fromMeta;
    return icpRawThumbnailUrl(canisterId, tokenIndex);
}
/** True when an <img> loaded but is effectively blank (1×1 / zero-size placeholders). */
export function isLikelyBlankImage(img) {
    return (!Number.isFinite(img.naturalWidth) ||
        !Number.isFinite(img.naturalHeight) ||
        img.naturalWidth < 2 ||
        img.naturalHeight < 2);
}
/** Entrepot asset proxy — unsafe to trust as a collection-specific thumbnail. */
export function isEntrepotProxyUrl(url) {
    try {
        const host = new URL(url).hostname.toLowerCase();
        return host.startsWith(`${ENTREPOT_PROXY}.`) || host.includes(ENTREPOT_PROXY);
    }
    catch {
        return /hdem4-ryaaa-aaaam-qa4xa-cai/i.test(url);
    }
}
/**
 * Only cache thumbs that came from the collection (or an unwrapped asset file).
 * Never cache Entrepot proxy URLs — they mis-attribute art across collections.
 */
export function isTrustedIcpThumbCacheUrl(url, collectionCanisterId) {
    if (!url?.trim() || isEntrepotProxyUrl(url))
        return false;
    if (url.trim().toLowerCase().startsWith("data:image/"))
        return true;
    try {
        const u = new URL(url);
        const host = u.hostname.toLowerCase();
        const cid = collectionCanisterId.toLowerCase();
        if (host.startsWith(`${cid}.`))
            return true;
        // Bonsai Bazaar collection_assets /c/{id}/{serial}/image
        if ((host.startsWith("n42s3-syaaa-aaaau-agwqa-cai.") ||
            host.includes("n42s3-syaaa-aaaau-agwqa-cai")) &&
            /\/c\/\d+\/\d+\//i.test(u.pathname)) {
            return true;
        }
        // Yumi/Yuku-style asset canisters: …/file/N.png or …/file/N_thumb.jpeg
        if (/\/file\/\d+(_thumb)?\.(png|jpe?g|webp|gif)$/i.test(u.pathname)) {
            return true;
        }
        // Toniq-style layered assets: ?asset=Trait_Name.png
        if (u.searchParams.has("asset") && host.startsWith(`${cid}.`)) {
            return true;
        }
        return false;
    }
    catch {
        return false;
    }
}
/**
 * Many EXT/raw gateways return an HTML viewer page with a nested <img src>, not a
 * direct image. Browser <img> tags cannot render that HTML — extract the nested URL.
 * Toniq-style canisters also emit SVG composites with `<image href="?asset=…">`.
 */
export function extractImgSrcFromHtml(html) {
    if (!html?.trim())
        return null;
    const patterns = [
        /<img\b[^>]*\bsrc\s*=\s*['"]([^'"]+)['"]/i,
        /<meta\b[^>]*\bproperty\s*=\s*['"]og:image['"][^>]*\bcontent\s*=\s*['"]([^'"]+)['"]/i,
        /<meta\b[^>]*\bcontent\s*=\s*['"]([^'"]+)['"][^>]*\bproperty\s*=\s*['"]og:image['"]/i,
        // Single-layer SVG fallback (composites need full SVG inlining — see below).
        /<image\b[^>]*\b(?:xlink:)?href\s*=\s*['"]([^'"]+)['"]/i,
    ];
    for (const re of patterns) {
        const m = html.match(re);
        const src = m?.[1]?.trim();
        if (src && (src.startsWith("http") || src.startsWith("data:") || src.startsWith("//"))) {
            return src.startsWith("//") ? `https:${src}` : src;
        }
    }
    return null;
}
/**
 * EXT multi-chunk ledgers (e.g. GG Guitar) return a meta refresh to a child
 * asset canister instead of an <img>. Extract that URL for materialize.
 */
export function extractMetaRefreshUrl(html) {
    if (!html?.trim())
        return null;
    const patterns = [
        /<meta\b[^>]*\bhttp-equiv\s*=\s*['"]?\s*refresh['"]?[^>]*\bcontent\s*=\s*['"][^'"]*URL\s*=\s*([^'"\s>]+)/i,
        /<meta\b[^>]*\bcontent\s*=\s*['"][^'"]*URL\s*=\s*([^'"\s>]+)[^'"]*['"][^>]*\bhttp-equiv\s*=\s*['"]?\s*refresh/i,
        /http-equiv\s*=\s*["']?refresh["']?[^>]*content\s*=\s*["']?\d+\s*;\s*URL\s*=\s*([^"'\s>]+)/i,
    ];
    for (const re of patterns) {
        const m = html.match(re);
        const raw = m?.[1]?.trim();
        if (!raw)
            continue;
        const url = raw.replace(/^['"]|['"]$/g, "");
        if (url.startsWith("http://") || url.startsWith("https://"))
            return url;
        if (url.startsWith("//"))
            return `https:${url}`;
    }
    return null;
}
function looksLikeSvgDocument(body) {
    const t = body.trimStart().slice(0, 800).toLowerCase();
    return (t.includes("<svg") ||
        (t.startsWith("<?xml") && t.includes("<svg")));
}
function looksLikeStatsTextPage(body) {
    return /cycle balance\s*:/i.test(body) && /minted nfts\s*:/i.test(body);
}
function bytesToBase64(bytes) {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    let out = "";
    for (let i = 0; i < bytes.length; i += 3) {
        const a = bytes[i];
        const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
        const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
        const triple = (a << 16) | (b << 8) | c;
        out += alphabet[(triple >> 18) & 63];
        out += alphabet[(triple >> 12) & 63];
        out += i + 1 < bytes.length ? alphabet[(triple >> 6) & 63] : "=";
        out += i + 2 < bytes.length ? alphabet[triple & 63] : "=";
    }
    return out;
}
function utf8ToBase64(text) {
    return bytesToBase64(new TextEncoder().encode(text));
}
/**
 * Toniq / Daku-style EXT: `?tokenid=` returns an SVG that layers traits via
 * `?asset=Name.png`. `<img>` cannot use text/html SVG with external hrefs —
 * fetch each asset and rewrite into a self-contained data:image/svg+xml URL.
 */
export async function materializeCompositedSvgPreview(svgText, fetchImpl = fetch) {
    if (!looksLikeSvgDocument(svgText))
        return null;
    const hrefRe = /(<image\b[^>]*\b(?:xlink:)?href\s*=\s*['"])([^'"]+)(['"])/gi;
    const urls = new Set();
    let m;
    const re = new RegExp(hrefRe.source, "gi");
    while ((m = re.exec(svgText)) !== null) {
        const href = m[2]?.trim();
        if (href && /^https?:\/\//i.test(href))
            urls.add(href);
    }
    let rewritten = svgText;
    for (const href of urls) {
        try {
            const res = await fetchImpl(href, {
                method: "GET",
                headers: { Accept: "image/*,*/*" },
                redirect: "follow",
            });
            if (!res.ok)
                continue;
            const ctype = (res.headers.get("content-type") || "image/png")
                .split(";")[0]
                .trim()
                .toLowerCase();
            if (ctype.includes("text/html") || ctype.includes("text/plain"))
                continue;
            const buf = new Uint8Array(await res.arrayBuffer());
            if (buf.length < 32)
                continue;
            const dataUrl = `data:${ctype || "image/png"};base64,${bytesToBase64(buf)}`;
            rewritten = rewritten.split(href).join(dataUrl);
        }
        catch {
            /* leave external href — preview may still partially render via object */
        }
    }
    // If we couldn't inline anything and externals remain, data: SVG in <img> will
    // often blank — still return data URL only when at least one asset inlined OR
    // there were no external https hrefs.
    const stillExternal = /<image\b[^>]*href\s*=\s*['"]https?:\/\//i.test(rewritten);
    const hadExternals = urls.size > 0;
    if (hadExternals && stillExternal && rewritten === svgText)
        return null;
    return `data:image/svg+xml;base64,${utf8ToBase64(rewritten)}`;
}
function looksLikeIcpHttpGateway(url) {
    try {
        const host = new URL(url).hostname.toLowerCase();
        return (host.endsWith(".icp0.io") ||
            host.endsWith(".ic0.app") ||
            host.endsWith(".raw.icp0.io") ||
            host.endsWith(".raw.ic0.app") ||
            host.includes(".raw."));
    }
    catch {
        return false;
    }
}
function isDirectImagePath(url) {
    try {
        return /\.(png|jpe?g|gif|webp|svg|bmp|avif)(\?|#|$)/i.test(new URL(url).pathname);
    }
    catch {
        return false;
    }
}
function isDirectVideoPath(url) {
    try {
        return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(new URL(url).pathname);
    }
    catch {
        return false;
    }
}
function contentTypeLooksLikeImage(ctype) {
    const c = ctype.toLowerCase();
    return (c.includes("image/") ||
        c.includes("application/octet-stream") ||
        c.includes("png") ||
        c.includes("jpeg") ||
        c.includes("jpg") ||
        c.includes("gif") ||
        c.includes("webp") ||
        c.includes("svg"));
}
function isHttpGatewayQueryUrl(url) {
    try {
        const u = new URL(url);
        return (u.searchParams.has("tokenid") ||
            u.searchParams.has("index") ||
            u.searchParams.has("type") ||
            u.pathname === "/" ||
            u.pathname === "");
    }
    catch {
        return false;
    }
}
/** Prefer Yumi/Yuku `_thumb.jpeg` sibling when a full `.png` was unwrapped from a thumb request. */
function preferThumbSibling(url, wantThumb) {
    if (!wantThumb)
        return url;
    try {
        const u = new URL(url);
        if (/\/file\/\d+\.png$/i.test(u.pathname)) {
            u.pathname = u.pathname.replace(/\.png$/i, "_thumb.jpeg");
            return u.toString();
        }
    }
    catch {
        /* keep */
    }
    return url;
}
/**
 * Materialize a URL into something an <img> / <video> can load.
 * EXT/raw gateways often return a tiny HTML viewer with a nested <img src>
 * (e.g. Revelation Walls / Yumi → asset canister /file/N.png or N_thumb.jpeg).
 * Toniq EXT (e.g. Daku Motoko) returns SVG composites with `?asset=` layer PNGs.
 * GG Guitar returns meta refresh → child asset canister.
 */
export async function materializeIcpImageUrl(url, fetchImpl = fetch, depth = 0) {
    const resolved = resolveIcpNftImageUrl(url);
    if (!resolved)
        return null;
    if (resolved.startsWith("data:"))
        return resolved;
    if (isEntrepotProxyUrl(resolved))
        return null;
    if (!looksLikeIcpHttpGateway(resolved))
        return resolved;
    if (isDirectImagePath(resolved) || isDirectVideoPath(resolved))
        return resolved;
    if (!isHttpGatewayQueryUrl(resolved))
        return resolved;
    let wantThumb = false;
    try {
        wantThumb = new URL(resolved).searchParams.get("type") === "thumbnail";
    }
    catch {
        /* */
    }
    try {
        const res = await fetchImpl(resolved, {
            method: "GET",
            headers: { Accept: "image/*,video/*,text/html,*/*" },
            redirect: "follow",
        });
        if (!res.ok)
            return null;
        const ctype = (res.headers.get("content-type") || "").toLowerCase();
        if (ctype.startsWith("video/") || ctype.startsWith("audio/")) {
            return resolved;
        }
        if (contentTypeLooksLikeImage(ctype) && !ctype.includes("text/html")) {
            return resolved;
        }
        if (ctype.includes("text/html") || ctype.includes("text/plain") || !ctype) {
            const html = await res.text();
            if (html.length > 256_000)
                return resolved;
            if (looksLikeStatsTextPage(html))
                return null;
            // GG Guitar / multi-chunk EXT: meta refresh → child asset canister
            const refresh = extractMetaRefreshUrl(html);
            if (refresh && refresh !== resolved && depth < 2) {
                return materializeIcpImageUrl(refresh, fetchImpl, depth + 1);
            }
            // Composited SVG (Toniq): inline ?asset= layers → data:image/svg+xml
            if (looksLikeSvgDocument(html)) {
                const dataUrl = await materializeCompositedSvgPreview(html, fetchImpl);
                if (dataUrl)
                    return dataUrl;
            }
            const nested = extractImgSrcFromHtml(html);
            if (nested && nested !== resolved) {
                // Nested may itself be ?asset= PNG (direct) or another gateway page.
                if (/[?&]asset=/i.test(nested) || isDirectImagePath(nested)) {
                    return preferThumbSibling(nested, wantThumb);
                }
                if (depth < 2 && looksLikeIcpHttpGateway(nested)) {
                    const deeper = await materializeIcpImageUrl(nested, fetchImpl, depth + 1);
                    if (deeper)
                        return deeper;
                }
                return preferThumbSibling(nested, wantThumb);
            }
            return null;
        }
        return resolved;
    }
    catch {
        return resolved;
    }
}
/**
 * Ordered thumbnail URL candidates for Yumi/EXT-era recovery.
 * Prefer collection raw `?tokenid=` (HTML unwrap → asset /file/N_thumb.jpeg).
 * Do not use Entrepot by default — it frequently serves the wrong collection's art.
 */
export function buildIcpThumbnailCandidates(canisterId, tokenIndex, opts = {}) {
    const idx = typeof tokenIndex === "bigint" ? Number(tokenIndex) : Number(tokenIndex);
    const out = [];
    const push = (url) => {
        const u = url?.trim();
        if (!u || out.includes(u))
            return;
        if (isEntrepotProxyUrl(u) && opts.includeEntrepotProxy !== true)
            return;
        out.push(u);
    };
    const preferred = resolveIcpNftImageUrl(opts.preferred);
    if (preferred && !isEntrepotProxyUrl(preferred))
        push(preferred);
    const addCanisterRoutes = (cid) => {
        const preferFull = opts.preferFullAssetBeforeThumbnail === true;
        // Yumi/Yuku EXT: ?tokenid= returns an HTML viewer → nested asset URL.
        // GG Guitar: ?index= meta-refreshes to child canister; type=thumbnail often 503s.
        if (opts.extTokenId) {
            if (preferFull) {
                push(icpTokenIdAssetUrl(cid, opts.extTokenId));
                push(`https://${cid}.raw.ic0.app/?tokenid=${encodeURIComponent(opts.extTokenId)}`);
                push(icpTokenIdThumbnailUrl(cid, opts.extTokenId));
                push(`https://${cid}.raw.ic0.app/?tokenid=${encodeURIComponent(opts.extTokenId)}&type=thumbnail`);
            }
            else {
                push(icpTokenIdThumbnailUrl(cid, opts.extTokenId));
                push(icpTokenIdAssetUrl(cid, opts.extTokenId));
                push(`https://${cid}.raw.ic0.app/?tokenid=${encodeURIComponent(opts.extTokenId)}&type=thumbnail`);
                push(`https://${cid}.raw.ic0.app/?tokenid=${encodeURIComponent(opts.extTokenId)}`);
            }
        }
        if (preferFull) {
            push(icpRawAssetUrl(cid, idx));
            push(`https://${cid}.raw.ic0.app/?index=${idx}`);
            push(icpRawThumbnailUrl(cid, idx));
            push(icpStaticThumbPathUrl(cid, idx));
            push(`https://${cid}.raw.ic0.app/?index=${idx}&type=thumbnail`);
        }
        else {
            push(icpRawThumbnailUrl(cid, idx));
            push(icpRawAssetUrl(cid, idx));
            push(icpStaticThumbPathUrl(cid, idx));
            push(`https://${cid}.raw.ic0.app/?index=${idx}&type=thumbnail`);
            push(`https://${cid}.raw.ic0.app/?index=${idx}`);
        }
        if (opts.includeEntrepotProxy === true) {
            push(icpEntrepotThumbnailUrl(cid, idx));
            push(`https://${ENTREPOT_PROXY}.raw.ic0.app/?canister=${cid}&index=${idx}&type=thumbnail`);
        }
    };
    addCanisterRoutes(canisterId);
    if (opts.includeWrapperOriginal !== false) {
        const original = EXT_WRAPPER_TO_ORIGINAL[canisterId];
        if (original)
            addCanisterRoutes(original);
    }
    return out;
}
function pickImageFromObject(obj) {
    for (const key of IMAGE_KEYS) {
        const value = obj[key];
        if (typeof value === "string" && value.trim()) {
            return resolveIcpNftImageUrl(value);
        }
    }
    const props = obj.properties;
    if (props && typeof props === "object" && !Array.isArray(props)) {
        for (const key of IMAGE_KEYS) {
            const value = props[key];
            if (typeof value === "string" && value.trim()) {
                return resolveIcpNftImageUrl(value);
            }
        }
    }
    return null;
}
export function imageFromMetadataBlob(blob) {
    if (!blob?.length)
        return null;
    let decoded;
    try {
        decoded = new TextDecoder("utf-8", { fatal: true }).decode(blob).trim();
    }
    catch {
        try {
            decoded = new TextDecoder("utf-8").decode(blob).trim();
        }
        catch {
            return null;
        }
    }
    if (!decoded)
        return null;
    if (decoded.startsWith("http") ||
        decoded.startsWith("data:") ||
        decoded.startsWith("ipfs://") ||
        decoded.startsWith("ar://") ||
        isBareIpfsCid(decoded)) {
        return resolveIcpNftImageUrl(decoded);
    }
    if (decoded.startsWith("{")) {
        try {
            const parsed = JSON.parse(decoded);
            return pickImageFromObject(parsed);
        }
        catch {
            return null;
        }
    }
    return null;
}
